'use client';
import InlineStyle from '@/components/InlineStyle';
import { motion } from 'framer-motion';
import { fadeUp, staggerContainer } from '@/lib/motion';

/**
 * PageHero — canonical interior-page hero, matching the homepage design
 * language (soft brand-blue→white wash, blue eyebrow, big navy headline with
 * optional Instrument-Serif italic accent, brand-blue CTAs, spring entrance).
 *
 * Replaces the per-page inline heroes + the retired dark/per-locale
 * components/PageHero.js. One design for every locale; copy still flows
 * through t() at the call site. RTL is handled by the global [dir="rtl"] rules
 * + logical flex/grid flow.
 *
 * Props:
 *   eyebrow   - small blue uppercase label (string)
 *   title     - headline node (embed <span className="serif-accent">…</span> for the italic accent)
 *   subtitle  - lede node
 *   actions   - CTA node (use <Button size="lg" />)
 *   visual    - optional right-side image/element; when present the hero is a 2-col split
 *   children  - extra content under the CTAs (e.g. a jump-link bar)
 *   align     - "center" (default when no visual) | "left" (default when visual present)
 */
export function PageHero({
  eyebrow,
  title,
  subtitle,
  actions,
  visual,
  children,
  align,
  className = '',
}) {
  const hasVisual = Boolean(visual);
  const center = (align || (hasVisual ? 'left' : 'center')) === 'center';

  return (
    <section
      className={className}
      style={{
        position: 'relative',
        overflow: 'hidden',
        background: 'var(--gradient-page)',
        padding: 'var(--hero-pad)',
      }}
    >
      <div
        aria-hidden="true"
        style={{ position: 'absolute', inset: 0, pointerEvents: 'none', background: 'var(--gradient-brand-hero)' }}
      />

      <div className="container" style={{ position: 'relative', zIndex: 1 }}>
        <div className={hasVisual ? 'page-hero-grid' : undefined}>
          <motion.div
            variants={staggerContainer(0.12)}
            initial="hidden"
            animate="show"
            style={{
              textAlign: center ? 'center' : 'left',
              maxWidth: center ? 780 : 560,
              margin: center ? '0 auto' : 0,
            }}
          >
            {eyebrow && (
              <motion.span variants={fadeUp} className="lb-eyebrow">{eyebrow}</motion.span>
            )}

            {title && (
              <motion.h1
                variants={fadeUp}
                style={{
                  fontWeight: 800,
                  fontSize: 'clamp(36px, 5.2vw, 60px)',
                  lineHeight: 1.06,
                  letterSpacing: '-0.02em',
                  color: 'var(--brand-deep)',
                  margin: '0 0 18px',
                }}
              >
                {title}
              </motion.h1>
            )}

            {subtitle && (
              <motion.div
                variants={fadeUp}
                style={{
                  fontSize: 17,
                  lineHeight: 1.7,
                  color: 'var(--text-body)',
                  maxWidth: 640,
                  margin: center ? '0 auto 32px' : '0 0 32px',
                }}
              >
                {subtitle}
              </motion.div>
            )}

            {actions && (
              <motion.div
                variants={fadeUp}
                style={{
                  display: 'flex',
                  gap: 14,
                  flexWrap: 'wrap',
                  justifyContent: center ? 'center' : 'flex-start',
                }}
              >
                {actions}
              </motion.div>
            )}

            {children && <motion.div variants={fadeUp} style={{ marginTop: 28 }}>{children}</motion.div>}
          </motion.div>

          {hasVisual && (
            <motion.div variants={fadeUp} initial="hidden" animate="show">
              {visual}
            </motion.div>
          )}
        </div>
      </div>

      <InlineStyle css={`
        .page-hero-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 48px;
          align-items: center;
        }
        @media (max-width: 900px) {
          .page-hero-grid { grid-template-columns: 1fr !important; gap: 32px !important; }
        }
      `} />
    </section>
  );
}
